/**
 * Page /grc/locations — Suivi des locations & cautions (tranche 4).
 *
 * Destinée au secrétariat (agent, manager, adjoints, maire) : visualiser les
 * réservations de matériel et, surtout, confirmer la restitution des cautions.
 * Une personne sans accès au Service technique peut ainsi gérer les cautions
 * depuis l'espace GRC.
 *
 * Couplée à la relance « caution à rendre » : quand un matériel est retourné,
 * l'agent configuré reçoit une cloche → il vient ici cocher « caution rendue ».
 */

import { useEffect, useState } from 'react';
import { api, ApiException } from '@/lib/api';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { Card, Button, Badge, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { ClesRemiseSelect } from '@/components/ClesRemiseSelect';
import {
  ClipboardCheck, CalendarDays, Euro, User,
  CheckCircle2, AlertCircle, Inbox,
} from 'lucide-react';
import type { MaterielReservation } from '@roronoa-collectivites/shared-types';
import { RESERVATION_STATUT } from '@/pages/materiel/MaterielReservationPages';
import { rafraichirNotifications } from '@/components/NotificationBell';

function formatDateFr(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}
function formatDateTimeFr(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}
function euros(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

type Bucket = 'a_rendre' | 'rendue' | 'en_cours' | 'litige' | 'toutes';

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: 'a_rendre', label: 'Cautions à traiter' },
  { key: 'en_cours', label: 'Locations en cours' },
  { key: 'rendue',   label: 'Cautions rendues' },
  { key: 'litige',   label: 'Non rendues / encaissées' },
  { key: 'toutes',   label: 'Toutes' },
];

function estACautionARendre(r: MaterielReservation): boolean {
  return r.statut === 'retournee' && r.cautionEuros > 0 && (r.cautionStatut === 'en_attente');
}

export function GrcSuiviLocationsPage() {
  const { confirm, prompt, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<MaterielReservation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bucket, setBucket] = useState<Bucket>('a_rendre');
  const [busyId, setBusyId] = useState<string | null>(null);
  // Paiement au RETOUR : moyen choisi avant de cliquer « Encaisser », par réservation.
  const [moyenRetour, setMoyenRetour] = useState<Record<string, 'cheque' | 'espece' | 'cb' | 'virement' | 'prelevement'>>({});
  // 9.2 — les clés cochées avant de cliquer « Remettre les clés », par réservation.
  // Vide = remise non tracée au détail, ce qui reste permis.
  const [clesChoisies, setClesChoisies] = useState<Record<string, string[]>>({});

  async function reload() {
    setLoading(true); setError(null);
    try { setItems(await api.materiel.reservations.list()); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  async function remettreCles(r: MaterielReservation) {
    const ok = await confirm({
      title: 'Remettre les clés ?',
      description: `${r.numero} — la remise des clés démarre l'occupation et acte l'encaissement de la caution (${euros(r.cautionEuros)} €). La facture de location sera émise et restera à encaisser (paiement au retour).`,
      confirmLabel: 'Clés remises',
    });
    if (!ok) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.updateStatut(r.id, {
        statut: 'remise',
        // 9.2 — vide quand la commune n'a pas encore déclaré ses clés : la
        // remise reste possible, elle n'est simplement pas tracée au détail.
        cleIds: clesChoisies[r.id] ?? [],
      }); rafraichirNotifications();
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  // Encaissement au RETOUR : la personne paie en rendant les clés (chèque/CB/espèces).
  async function encaisserAuRetour(r: MaterielReservation) {
    const moyen = moyenRetour[r.id] ?? 'cheque';
    const ok = await confirm({
      title: 'Encaisser la location ?',
      description: `${r.numero} — encaissement de la location (${euros(r.montantEuros ?? 0)} €) par ${moyen}. La facture passera « payée ».`,
      confirmLabel: 'Encaisser',
    });
    if (!ok) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.encaisserLocation(r.id, moyen);
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function marquerRetour(r: MaterielReservation) {
    const ok = await confirm({
      title: 'Récupérer les clés (retour) ?',
      description: `${r.numero} — clôture de l'occupation après contrôle d'état du Service Technique. La caution pourra ensuite être restituée.`,
      confirmLabel: 'Clés récupérées',
    });
    if (!ok) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.updateStatut(r.id, { statut: 'retournee' }); rafraichirNotifications();
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function cautionRendue(r: MaterielReservation) {
    const ok = await confirm({
      title: 'Confirmer la restitution de la caution ?',
      description: `${r.numero} — caution de ${euros(r.cautionEuros)} € rendue à ${r.demandeurNom ?? 'l\u2019administré'}. Cette action est enregistrée à votre nom.`,
      confirmLabel: 'Caution rendue',
    });
    if (!ok) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.cautionStatut(r.id, { statut: 'rendue' });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function cautionNonRendue(r: MaterielReservation) {
    const motif = await prompt({
      title: `Caution non rendue — ${r.numero}`,
      description: 'Indiquez le motif (dégât, ménage non fait, devis en cours…). Visible dans le suivi et modifiable ensuite.',
      defaultValue: r.cautionCommentaire ?? r.controleEtatNote ?? '',
      confirmLabel: 'Marquer non rendue',
    });
    if (motif === null) return;
    if (!motif.trim()) { setError('Un motif est requis pour une caution non rendue.'); return; }
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.cautionStatut(r.id, { statut: 'non_rendue', commentaire: motif.trim() });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function cautionEncaissee(r: MaterielReservation) {
    const motif = await prompt({
      title: `Caution encaissée — ${r.numero}`,
      description: 'Caution définitivement encaissée par la commune. Précisez le motif / montant (ex. « devis 350 € retenu »).',
      defaultValue: r.cautionCommentaire ?? '',
      confirmLabel: 'Marquer encaissée',
    });
    if (motif === null) return;
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.cautionStatut(r.id, { statut: 'encaissee', commentaire: motif.trim() || undefined });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  async function modifierCommentaireCaution(r: MaterielReservation) {
    const c = await prompt({
      title: `Suivi caution — ${r.numero}`,
      description: 'Faites vivre le dossier (ex. « devis reçu 350 € », « relance envoyée »).',
      defaultValue: r.cautionCommentaire ?? '',
      confirmLabel: 'Enregistrer',
    });
    if (c === null) return;
    const statut = (r.cautionStatut === 'en_attente' ? 'non_rendue' : r.cautionStatut) as 'rendue' | 'non_rendue' | 'encaissee';
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.cautionStatut(r.id, { statut, commentaire: c.trim() || undefined });
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur');
    } finally { setBusyId(null); }
  }

  const all = items ?? [];
  const nbARendre = all.filter(estACautionARendre).length;
  const filtered = all.filter((r) => {
    if (bucket === 'toutes') return true;
    if (bucket === 'a_rendre') return estACautionARendre(r);
    if (bucket === 'rendue') return r.cautionStatut === 'rendue';
    if (bucket === 'litige') return r.cautionStatut === 'non_rendue' || r.cautionStatut === 'encaissee';
    return r.statut === 'demande' || r.statut === 'validee' || r.statut === 'remise';
  });

  return (
    <GrcLayout active="locations">
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        {dialogElement}

        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={ClipboardCheck} accent="grc" size="lg" />
          <div>
            <SectionEyebrow as="div">Relation aux administrés</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              Suivi des cautions &amp; locations
            </h1>
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

        {nbARendre > 0 && (
          <Alert variant="warning" icon={Euro} className="mb-4">
            {nbARendre} caution{nbARendre > 1 ? 's' : ''} en attente de restitution.
          </Alert>
        )}

        <div className="mb-4 flex flex-wrap gap-2">
          {BUCKETS.map((b) => (
            <button
              key={b.key}
              type="button"
              onClick={() => setBucket(b.key)}
              className={
                'inline-flex h-8 items-center rounded-r-pill border px-3 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
                (bucket === b.key
                  ? 'border-grc/30 bg-grc-tint/80 text-grc-fg'
                  : 'border-border bg-surface/40 text-ink-muted hover:text-ink hover:border-border-strong')
              }
            >
              {b.label}
              {b.key === 'a_rendre' && nbARendre > 0 && (
                <span className="ml-1.5 rounded-r-pill bg-warning-tint px-1.5 text-warning">{nbARendre}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
        ) : filtered.length === 0 ? (
          <Card className="py-12 text-center">
            <Inbox className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
            <p className="mt-3 text-sm text-ink-muted">
              {bucket === 'a_rendre'
                ? 'Aucune caution en attente. Tout est à jour.'
                : 'Aucune location dans ce filtre.'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filtered.map((r) => {
              const st = RESERVATION_STATUT[r.statut];
              const aRendre = estACautionARendre(r);
              const busy = busyId === r.id;
              return (
                <Card key={r.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-sm font-semibold text-ink">{r.materielNom}</h3>
                        <Badge variant={st.variant}>{st.label}</Badge>
                      </div>
                      <p className="mt-0.5 text-xs text-ink-subtle">{r.numero}</p>
                    </div>
                    <span className="text-xs font-medium text-ink">
                      {r.quantite} unité{r.quantite > 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-1.5 border-t border-border pt-3 text-xs text-ink-muted sm:grid-cols-2">
                    <span className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {r.demandeurNom ?? 'Administré'}
                      {r.beneficiaireType !== 'perso' && r.beneficiaireNom && (
                        <span className="ml-1 rounded-r bg-surface-hi/60 px-1.5 py-0.5 text-2xs text-ink-subtle">
                          pour {r.beneficiaireType === 'association' ? 'l\u2019asso' : 'l\u2019enseigne'} {r.beneficiaireNom}
                        </span>
                      )}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {formatDateFr(r.dateRetrait)} → {formatDateFr(r.dateRetour)}
                    </span>
                  </div>

                  {/* Gestion des clés (salles uniquement) */}
                  {r.creneau != null && (r.statut === 'validee' || r.statut === 'remise') && (
                    <div className="mt-3 border-t border-border pt-3">
                      {r.statut === 'validee' ? (
                        <div className="space-y-3">
                          <p className="text-xs text-ink-subtle">Salle validée — à remettre les clés (la veille, {r.salleClesRetraitDebut || '16:00'}–{r.salleClesRetraitFin || '17:00'}).</p>
                          {/* 9.2 — désigner LESQUELLES, avant de cliquer. */}
                          <ClesRemiseSelect
                            reservationId={r.id}
                            aide="Cochez le ou les exemplaires que vous donnez. Ils figureront sur le récapitulatif de l'administré."
                            onChange={(ids) => setClesChoisies((m) => ({ ...m, [r.id]: ids }))}
                          />
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <Button variant="primary" size="sm" disabled={busy} onClick={() => void remettreCles(r)}>
                              Remettre les clés
                            </Button>
                          </div>
                        </div>
                      ) : r.controleEtatOk ? (
                        <div className="space-y-2">
                          <p className={`flex items-center gap-1.5 text-xs font-medium ${r.controleEtatConforme === false ? 'text-urgent-fg' : 'text-success-fg'}`}>
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                            {r.controleEtatConforme === false
                              ? 'Service Technique : état NON CONFORME'
                              : 'Service Technique : état conforme'}
                            {r.controleEtatParNom ? ` — ${r.controleEtatParNom}` : ''}
                          </p>
                          {r.controleEtatNote && (
                            <p className={`rounded-r-md p-2 text-xs ${r.controleEtatConforme === false ? 'bg-urgent-tint/40 text-urgent-fg' : 'bg-surface/60 text-ink-muted'}`}>
                              Observation : {r.controleEtatNote}
                            </p>
                          )}
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {(r.montantEuros ?? 0) > 0 && (
                              <div className="flex items-center gap-2 rounded-r-md border border-border bg-surface/30 px-2 py-1">
                                <span className="text-xs text-ink-muted">Paiement au retour :</span>
                                <select
                                  value={moyenRetour[r.id] ?? 'cheque'}
                                  onChange={(e) => setMoyenRetour((m) => ({ ...m, [r.id]: e.target.value as 'cheque' | 'espece' | 'cb' | 'virement' | 'prelevement' }))}
                                  className="h-8 rounded-r-md border border-border bg-surface/40 px-2 text-xs text-ink"
                                >
                                  <option value="cheque">Chèque</option>
                                  <option value="espece">Espèces</option>
                                  <option value="cb">Carte bancaire</option>
                                  <option value="virement">Virement</option>
                                </select>
                                <Button variant="secondary" size="sm" disabled={busy} onClick={() => void encaisserAuRetour(r)}>
                                  Encaisser ({euros(r.montantEuros ?? 0)} €)
                                </Button>
                              </div>
                            )}
                            <Button variant="primary" size="sm" disabled={busy} onClick={() => void marquerRetour(r)}>
                              Récupérer les clés (retour)
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-ink-subtle">
                          Clés remises — en attente du contrôle d'état par le Service Technique avant le retour.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Observation de contrôle persistante (visible jusqu'à la décision caution) */}
                  {r.creneau != null && r.controleEtatConforme === false && r.controleEtatNote && !r.cautionRendue && r.statut === 'retournee' && (
                    <div className="mt-3 rounded-r-md border border-urgent/30 bg-urgent-tint/30 p-2 text-xs text-urgent-fg">
                      ⚠️ État non conforme signalé par le Service Technique : {r.controleEtatNote}
                    </div>
                  )}

                  {/* État de la caution */}
                  <div className="mt-3 border-t border-border pt-3">
                    {r.cautionStatut === 'rendue' ? (
                      <p className="flex items-start gap-1.5 text-xs text-success-fg">
                        <CheckCircle2 className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
                        <span>
                          Caution de {euros(r.cautionEuros)} € rendue
                          {r.cautionRendueAt ? ` le ${formatDateTimeFr(r.cautionRendueAt)}` : ''}
                          {r.cautionRenduePar ? ` par ${r.cautionRenduePar}` : ''}.
                        </span>
                      </p>
                    ) : (r.cautionStatut === 'non_rendue' || r.cautionStatut === 'encaissee') ? (
                      <div className="space-y-1.5">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-urgent-fg">
                          <Euro className="h-4 w-4" aria-hidden="true" />
                          Caution de {euros(r.cautionEuros)} € — {r.cautionStatut === 'encaissee' ? 'encaissée par la commune' : 'non rendue (litige en cours)'}.
                        </p>
                        {r.cautionCommentaire && (
                          <p className="rounded-r-md bg-urgent-tint/30 p-2 text-xs text-urgent-fg">Suivi : {r.cautionCommentaire}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void modifierCommentaireCaution(r)}>
                            Mettre à jour le suivi
                          </Button>
                          {r.cautionStatut === 'non_rendue' && (
                            <>
                              <Button variant="ghost" size="sm" disabled={busy} onClick={() => void cautionEncaissee(r)}>
                                Encaisser
                              </Button>
                              <Button variant="ghost" size="sm" className="text-success-fg" disabled={busy} onClick={() => void cautionRendue(r)}>
                                Finalement rendue
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ) : aRendre ? (
                      <div className="space-y-2">
                        <p className="flex items-center gap-1.5 text-xs font-medium text-warning">
                          <Euro className="h-4 w-4" aria-hidden="true" />
                          Caution de {euros(r.cautionEuros)} € à traiter.
                        </p>
                        {r.creneau != null && r.controleEtatConforme === false && r.controleEtatNote && (
                          <p className="rounded-r-md bg-urgent-tint/30 p-2 text-xs text-urgent-fg">
                            ⚠️ État non conforme (Service Technique) : {r.controleEtatNote}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <Button variant="primary" size="sm" disabled={busy} onClick={() => void cautionRendue(r)}>
                            <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Rendre la caution
                          </Button>
                          <Button variant="ghost" size="sm" className="text-urgent-fg" disabled={busy} onClick={() => void cautionNonRendue(r)}>
                            Non rendue
                          </Button>
                          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void cautionEncaissee(r)}>
                            Encaisser
                          </Button>
                        </div>
                      </div>
                    ) : r.cautionEuros > 0 ? (
                      <p className="text-xs text-ink-subtle">
                        Caution de {euros(r.cautionEuros)} € — traitement possible après le retour.
                      </p>
                    ) : (
                      <p className="text-xs text-ink-subtle">Pas de caution sur cette location.</p>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </GrcLayout>
  );
}
